// src/redux/slices/auctionsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { showNotification } from './notificationSlice';
import API_ROUTES from "../../config/apiRoutes";

// Fetch all auctions with filters and pagination
export const fetchAuctions = createAsyncThunk(
  'auctions/fetchAuctions',
  async (filterParams = {}, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const payload = {
        currentPage: filterParams.currentPage || 1,
        limit: filterParams.limit || 10,
        status: filterParams.status || '', // live, completed, blocked
        searchTerm: filterParams.searchTerm || '',
        startDate: filterParams.startDate || '',
        endDate: filterParams.endDate || '',
        country: filterParams.country || '',
        ...filterParams
      };

      console.log('Auction API Request Payload:', payload);

      const response = await axios.post(
        API_ROUTES.Users.GET_AUCTIONS,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log('Auction API Response:', response.data);

      // Handle different possible response structures
      const responseData = response.data;
      let auctions = [];
      let total = 0;
      let totalPages = 1;

      if (responseData.data && Array.isArray(responseData.data)) {
        auctions = responseData.data;
        total = responseData.total || responseData.data.length;
        totalPages = responseData.totalPages || Math.ceil(total / payload.limit);
      } else if (Array.isArray(responseData)) {
        auctions = responseData;
        total = responseData.length;
        totalPages = Math.ceil(total / payload.limit);
      } else if (responseData.auctions && Array.isArray(responseData.auctions)) {
        auctions = responseData.auctions;
        total = responseData.total || responseData.auctions.length;
        totalPages = responseData.totalPages || Math.ceil(total / payload.limit);
      }

      console.log('Processed auction data:', { auctions: auctions.length, total, totalPages });

      return {
        auctions,
        total,
        totalPages,
        currentPage: payload.currentPage
      };
    } catch (error) {
      console.error('Auction API Error:', error);
      return rejectWithValue(error.response?.data?.message || "Failed to fetch auctions");
    }
  }
);

// Fetch single auction details
export const fetchAuctionDetails = createAsyncThunk(
  'auctions/fetchAuctionDetails',
  async (auctionId, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.get(
        `${API_ROUTES.Users.GET_AUCTIONS}/${auctionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch auction details");
    }
  }
);

// Update auction status
export const updateAuctionStatus = createAsyncThunk(
  'auctions/updateAuctionStatus',
  async ({ auctionId, status }, { getState, dispatch, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      await axios.patch(
        API_ROUTES.Users.UPDATE_STATUS(auctionId),
        { status },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      dispatch(showNotification({ 
        message: `Auction ${status} successfully!`, 
        type: "success" 
      }));
      return { auctionId, status };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update auction status";
      dispatch(showNotification({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

const auctionsSlice = createSlice({
  name: 'auctions',
  initialState: {
    auctions: [],
    total: 0,
    totalPages: 1,
    currentPage: 1,
    loading: false,
    error: null,
    
    // Single auction details
    auctionDetails: null,
    loadingDetails: false,
    errorDetails: null,
  },
  reducers: {
    clearAuctionDetails: (state) => {
      state.auctionDetails = null;
      state.errorDetails = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch auctions
      .addCase(fetchAuctions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuctions.fulfilled, (state, action) => {
        state.loading = false;
        state.auctions = action.payload.auctions;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchAuctions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch auction details
      .addCase(fetchAuctionDetails.pending, (state) => {
        state.loadingDetails = true;
        state.errorDetails = null;
      })
      .addCase(fetchAuctionDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.auctionDetails = action.payload;
      })
      .addCase(fetchAuctionDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.errorDetails = action.payload;
      })
      
      // Update auction status
      .addCase(updateAuctionStatus.fulfilled, (state, action) => {
        const { auctionId, status } = action.payload;
        state.auctions = state.auctions.map(auction =>
          auction._id === auctionId ? { ...auction, status } : auction
        );
      });
  },
});

export const { clearAuctionDetails, setCurrentPage } = auctionsSlice.actions;
export default auctionsSlice.reducer;